import React from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import PageHeader from "@/components/PageHeader";

export default function Roadmap() {
  const { t, lang } = useLanguage();
  const sequences = t("roadmap.sequences");

  const stateMeta = (state) => {
    if (state === "complete")
      return { label: t("roadmap.statusComplete"), cls: "text-mizu-cyan border-mizu-cyan/50 bg-mizu-cyan/10" };
    if (state === "active")
      return { label: t("roadmap.statusActive"), cls: "text-mizu-ice border-mizu-ice/40 bg-mizu-ice/5" };
    return { label: t("roadmap.statusEncrypted"), cls: "text-mizu-faint border-mizu-faint/30 bg-mizu-faint/5" };
  };

  return (
    <div data-testid="roadmap-page" className="relative">
      <PageHeader
        label={t("roadmap.label")}
        title={t("roadmap.title")}
        sub={t("roadmap.sub")}
      />

      <section className="mx-auto max-w-4xl px-4 pb-24 pt-14 sm:px-6">
        <div className="relative">
          <div className="absolute bottom-6 left-[22px] top-6 w-px bg-gradient-to-b from-mizu-cyan/60 via-mizu-cyan/15 to-mizu-faint/10" />

          {sequences.map((seq, i) => {
            const meta = stateMeta(seq.state);
            const encrypted = seq.state === "encrypted";
            return (
              <motion.div
                key={seq.num}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className="relative mb-8 pl-14"
                data-testid={`roadmap-sequence-${seq.num}`}
              >
                {/* node marker */}
                <div
                  className={`absolute left-[15px] top-6 h-4 w-4 border ${
                    encrypted
                      ? "border-mizu-faint/40 bg-[#020611]"
                      : "border-mizu-cyan bg-[#020611] shadow-[0_0_14px_rgba(0,240,255,0.55)]"
                  }`}
                />

                <div
                  className={`mizu-panel mizu-corners p-6 ${encrypted ? "opacity-70" : "mizu-card"}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.35em] text-mizu-cyan/70">
                        SEQUENCE {seq.num}
                      </div>
                      <h3 className={`mt-1 font-display text-xl font-bold tracking-[0.2em] ${encrypted ? "text-mizu-faint" : "text-white"} ${lang === "jp" ? "font-jp tracking-widest" : ""}`}>
                        {seq.title}
                      </h3>
                    </div>
                    <span
                      className={`border px-2.5 py-1 font-mono text-[9px] tracking-[0.25em] ${meta.cls}`}
                      data-testid={`roadmap-status-${seq.num}`}
                    >
                      {meta.label}
                    </span>
                  </div>

                  {encrypted ? (
                    <div className="mt-5 flex items-center gap-3 border border-mizu-faint/20 bg-[#020611]/60 px-4 py-3 font-mono text-xs text-mizu-faint">
                      <Lock size={13} />
                      {t("roadmap.encrypted")}
                      <span className="animate-blink">_</span>
                    </div>
                  ) : (
                    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                      {seq.items.map((item) => (
                        <li
                          key={item}
                          className={`font-mono text-[11px] tracking-[0.12em] ${
                            seq.state === "complete" ? "text-mizu-cyan/85" : "text-mizu-dim"
                          }`}
                        >
                          {seq.state === "complete" ? "✓" : "◉"} {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
